import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Select from 'react-select';

const ReplaceDeveloperSalesModal = ({ show, handleClose }) => {
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState('existing');
    const [developers, setDevelopers] = useState([]);
    const [developerSales, setDeveloperSales] = useState([]);
    const [selectedDeveloper, setSelectedDeveloper] = useState(null);
    const [selectedDeveloperSales, setSelectedDeveloperSales] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteMobile, setInviteMobile] = useState('');

    const resetState = () => {
        setMode('existing');
        setSelectedDeveloper(null);
        setSelectedDeveloperSales(null);
        setInviteEmail('');
        setInviteMobile('');
    };

    useEffect(() => {
        if (show) {
            resetState();
            fetchDevelopers();
            fetchDeveloperSales();
        }
    }, [show]);

    const fetchDevelopers = async () => {
        try {
            const response = await api.get('/developers/unpaginated');
            if (response.data.status) {
                setDevelopers(response.data.response.data);
            }
        } catch (error) {
            console.error('Error fetching developers:', error);
            toast.error('Failed to fetch developers');
        }
    };

    const fetchDeveloperSales = async () => {
        try {
            const response = await api.get('/users?type=developerSales');
            if (response.data.status) {
                setDeveloperSales(response.data.response.data);
            }
        } catch (error) {
            console.error('Error fetching developer sales:', error);
            toast.error('Failed to fetch developer sales');
        }
    };

    const canSubmitExisting = () =>
        selectedDeveloper && selectedDeveloperSales;

    const canSubmitInvite = () => {
        const emailOk = inviteEmail.trim().length > 0 && /^\S+@\S+\.\S+$/.test(inviteEmail.trim());
        const mobileOk = inviteMobile.trim().length > 0;
        return selectedDeveloper && emailOk && mobileOk;
    };

    const handleSubmit = async () => {
        if (!selectedDeveloper) {
            toast.error('Please select a developer');
            return;
        }

        if (mode === 'existing') {
            if (!selectedDeveloperSales) {
                toast.error('Please select a developer sales user to assign');
                return;
            }
        } else {
            if (!canSubmitInvite()) {
                toast.error('Please enter a valid email and mobile number');
                return;
            }
        }

        setLoading(true);
        try {
            let body;
            if (mode === 'existing') {
                body = {
                    developer_id: selectedDeveloper.value,
                    new_developer_sales_id: selectedDeveloperSales.value,
                };
            } else {
                body = {
                    developer_id: selectedDeveloper.value,
                    email: inviteEmail.trim(),
                    mobile: inviteMobile.trim(),
                };
            }

            const response = await api.post('/developers/replace-sales', body);

            if (response.data.status) {
                if (mode === 'invite') {
                    toast.success(
                        'Developer sales updated. The new contact will receive credentials (check email).'
                    );
                } else {
                    toast.success('Developer sales replaced successfully');
                }
                handleClose();
            } else {
                toast.error(response.data.message || 'Failed to replace developer sales');
            }
        } catch (error) {
            console.error('Error replacing developer sales:', error);
            const msg = error.response?.data?.message || 'Failed to replace developer sales';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const developerOptions = developers.map((dev) => ({
        value: dev.id,
        label: dev.name,
    }));

    const developerSalesOptions = developerSales.map((sales) => ({
        value: sales.id,
        label: `${sales.name} (${sales.email || 'no email'})`,
    }));

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Replace Developer Sales</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Developer</Form.Label>
                        <Select
                            options={developerOptions}
                            value={selectedDeveloper}
                            onChange={setSelectedDeveloper}
                            placeholder="Select a developer..."
                            isClearable
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>How to replace</Form.Label>
                        <div>
                            <Form.Check
                                className="mb-2"
                                type="radio"
                                name="replaceMode"
                                id="mode-existing"
                                label="Assign an existing developer sales user"
                                checked={mode === 'existing'}
                                onChange={() => setMode('existing')}
                            />
                            <Form.Check
                                type="radio"
                                name="replaceMode"
                                id="mode-invite"
                                label="New contact (email + mobile — account updated in place, temp password sent)"
                                checked={mode === 'invite'}
                                onChange={() => setMode('invite')}
                            />
                        </div>
                    </Form.Group>

                    {mode === 'existing' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Existing developer sales</Form.Label>
                            <Select
                                options={developerSalesOptions}
                                value={selectedDeveloperSales}
                                onChange={setSelectedDeveloperSales}
                                placeholder="Select a developer sales user..."
                                isClearable
                            />
                        </Form.Group>
                    )}

                    {mode === 'invite' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="newsales@example.com"
                                    autoComplete="off"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Mobile</Form.Label>
                                <Form.Control
                                    type="tel"
                                    value={inviteMobile}
                                    onChange={(e) => setInviteMobile(e.target.value)}
                                    placeholder="01012345678"
                                    autoComplete="off"
                                />
                            </Form.Group>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={
                        loading ||
                        !selectedDeveloper ||
                        (mode === 'existing' && !canSubmitExisting()) ||
                        (mode === 'invite' && !canSubmitInvite())
                    }
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Processing...
                        </>
                    ) : mode === 'invite' ? (
                        'Save & send invite'
                    ) : (
                        'Assign sales user'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReplaceDeveloperSalesModal;
